import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type CurrentUser, type RequestListRow, type RequestStatus } from '../api';
import { Alert, Badge, Button, Card, EmptyState, PageHeader, Spinner } from '../components/ui';
import { IconClipboard } from '../components/icons';

const STATUS: Record<
  RequestStatus,
  { label: string; tone: 'neutral' | 'brand' | 'copper' | 'success' | 'warning' | 'danger' | 'info' }
> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  PENDING_APPROVAL: { label: 'Awaiting approval', tone: 'warning' },
  CHANGES_REQUESTED: { label: 'Changes requested', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'info' },
  IN_PROGRESS: { label: 'With IT', tone: 'copper' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export function MyRequests({ user }: { user: CurrentUser }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const requests = useQuery({ queryKey: ['requests', 'mine'], queryFn: () => api.listRequests('mine') });

  const create = useMutation({
    mutationFn: (type: 'ENROLMENT' | 'OFFBOARDING' = 'ENROLMENT') => api.createRequest(type),
    onSuccess: ({ request }) => {
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
      navigate(`/requests/${request.id}/edit`);
    },
  });

  if (requests.isPending) return <Spinner label="Loading your requests" />;
  if (requests.isError) {
    return <Alert>{requests.error instanceof ApiError ? requests.error.message : 'Could not load requests.'}</Alert>;
  }

  const rows = requests.data.requests;

  return (
    <div>
      <PageHeader
        title="My requests"
        description="Everything you have raised, and where each one has got to."
        actions={
          <>
            <Button variant="secondary" onClick={() => create.mutate('OFFBOARDING')} disabled={create.isPending}>
              Offboarding
            </Button>
            <Button onClick={() => create.mutate('ENROLMENT')} disabled={create.isPending}>
              {create.isPending ? 'Starting…' : 'New enrolment'}
            </Button>
          </>
        }
      />

      {create.isError && (
        <div className="mb-4">
          <Alert>{create.error instanceof ApiError ? create.error.message : 'Could not start a request.'}</Alert>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<IconClipboard />}
          title="No requests yet"
          body={`Start an enrolment when someone new joins ${
            user.department ? user.department.name : 'your department'
          }. It saves as you go, so you can gather the details over time.`}
          action={
            <Button onClick={() => create.mutate('ENROLMENT')} disabled={create.isPending}>
              New enrolment
            </Button>
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-3xl text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                  <th className="px-5 py-3">Reference</th>
                  <th className="px-5 py-3">Employee</th>
                  <th className="px-5 py-3">Department</th>
                  <th className="px-5 py-3">Start date</th>
                  <th className="px-5 py-3">Items</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Row({ row }: { row: RequestListRow }) {
  const status = STATUS[row.status];
  const target = row.status === 'DRAFT' || row.status === 'CHANGES_REQUESTED' ? `/requests/${row.id}/edit` : `/requests/${row.id}`;

  return (
    <tr className="border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
      <td className="px-5 py-3">
        <Link
          to={target}
          className="font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
        >
          {row.reference}
        </Link>
      </td>
      <td className="px-5 py-3 text-slate-900 dark:text-slate-100">
        {row.employeeName || <span className="text-slate-500 dark:text-slate-400">Not named yet</span>}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">{row.department ?? 'N/A'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
        {row.startDate ? new Date(row.startDate).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'N/A'}
      </td>
      <td className="px-5 py-3 tabular-nums text-slate-600 dark:text-slate-400">{row.itemCount}</td>
      <td className="px-5 py-3">
        <Badge tone={status.tone} dot>
          {status.label}
        </Badge>
      </td>
      <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
        {new Date(row.updatedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
      </td>
    </tr>
  );
}

export { STATUS as REQUEST_STATUS };
